import { useTranslation } from 'react-i18next';
import { Product } from '@haccp/api-client';
import { AuthenticatedImage } from '@/components/ui';

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ImageIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const PrintIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);

export function ProductList({
  product,
  onViewDetail,
  onPrintLabel,
  isPrinting,
}: {
  product: Product;
  onViewDetail: () => void;
  onPrintLabel?: () => void;
  isPrinting?: boolean;
}) {
  const { t, i18n } = useTranslation();

  const expiryDate = product.expiryDate ? new Date(product.expiryDate) : null;
  const hasValidExpiry = !!expiryDate && !Number.isNaN(expiryDate.getTime());
  const hasNoExpiry = hasValidExpiry && expiryDate.getFullYear() >= 9999;
  const today = new Date();
  const daysUntilExpiry = hasValidExpiry && !hasNoExpiry
    ? Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const isExpiringSoon =
    hasValidExpiry && !hasNoExpiry && daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  const isExpired = hasValidExpiry && !hasNoExpiry && daysUntilExpiry < 0;

  return (
    <div
      onClick={onViewDetail}
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition cursor-pointer overflow-hidden border border-slate-200 hover:border-blue-300"
    >
      {product.photos && product.photos.length > 0 ? (
        <div className="w-full h-40 bg-slate-100 overflow-hidden flex items-center justify-center">
          <AuthenticatedImage
            src={product.photos[0].photoUrl}
            alt={product.name}
            className="w-full h-full object-cover hover:scale-105 transition"
          />
        </div>
      ) : (
        <div className="w-full h-40 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-slate-300" />
        </div>
      )}

      <div className="p-4">
        <h3 className="font-semibold text-slate-900 text-lg mb-1 truncate">{product.name}</h3>
        <p className="text-sm text-slate-600 mb-3 truncate">
          {t('reception.supplier')} : {product.supplier}
        </p>

        <p className="text-xs text-slate-500 mb-2">
          {t('traceability.addedOn')}{' '}
          {new Date(product.createdAt).toLocaleDateString(i18n.language)}{' '}
          {new Date(product.createdAt).toLocaleTimeString(i18n.language, {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
        <p className="text-xs text-slate-500 mb-3">
          {t('traceability.addedBy')}{' '}
          <span className="text-slate-700">
            {product.creator?.displayName || product.creator?.email || t('traceability.unknown')}
          </span>
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
              isExpired
                ? 'bg-red-50 text-red-700'
                : isExpiringSoon
                  ? 'bg-yellow-50 text-yellow-700'
                  : 'bg-green-50 text-green-700'
            }`}
          >
            <CalendarIcon className="w-4 h-4" />
            {!hasValidExpiry || hasNoExpiry
              ? t('traceability.noExpiry')
              : isExpired
                ? t('traceability.expired')
                : isExpiringSoon
                  ? t('traceability.expiresIn', { count: daysUntilExpiry })
                  : t('traceability.validFor', { count: daysUntilExpiry })}
          </div>
          <ChevronRightIcon className="w-5 h-5 text-slate-400" />
        </div>

        {onPrintLabel && (
          <div className="pt-3 mt-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrintLabel();
              }}
              disabled={isPrinting}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PrintIcon className="w-4 h-4" />
              {isPrinting ? t('traceability.printing') : t('traceability.printLabel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
